import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';
import Draggable from 'react-draggable';
import cx from 'classnames';
import Icon from 'components/Icon';

import { FilePropType } from 'propTypes/common';

import { ImageFormatContext } from 'contexts/ImageFormatContext';

import styles from './styles.module.scss';

const MAX_CONTAINER_HEIGHT = 600;

const DoubleImage = ({ images }) => {
	const { first, second, text } = images;
	const [secondImageWidth, setSecondImageWidth] = useState(0);
	const [secondImageOffset, setSecondImageOffset] = useState(0);
	const [borderImageHeight, setBorderImageHeight] = useState(
		MAX_CONTAINER_HEIGHT
	);
	const [isButtonDefaultPosition, setIsButtonDefaultPosition] = useState(true);
	const [xAxisPosition, setXAxisPosition] = useState(0);
	const refMain = useRef(null);
	const refButton = useRef(null);
	const firstImageRef = useRef(null);
	const secondImageRef = useRef(null);
	const imageFormat = useContext(ImageFormatContext);

	const initialSecondImageWidth = () => refMain?.current?.offsetWidth / 2;

	const checkOffsetValue = (value) => (value > 0 ? 0 : value);

	const imagesBasicParams = {
		format: imageFormat,
		params: {
			crop: 'fill',
			gravity: 'center',
			width: 884
		}
	};

	const draggableZoneBoundaries = {
		top: 0,
		bottom: 0,
		left: -initialSecondImageWidth(),
		right: initialSecondImageWidth()
	};

	const imageSrcFirst = getImageUrl(first, imagesBasicParams);

	const imageSrcSecond = getImageUrl(second, imagesBasicParams);

	const currentButtonPosition = isButtonDefaultPosition
		? { x: 0, y: 0 }
		: { x: xAxisPosition, y: 0 };

	const setDefaultSecondImageParams = () => {
		setSecondImageWidth(initialSecondImageWidth());
		setSecondImageOffset(-initialSecondImageWidth());
		const renderedImage = firstImageRef?.current || secondImageRef?.current;
		const imageHeight = renderedImage
			? Math.round(renderedImage?.getBoundingClientRect().height)
			: 0;
		const maxImageHeight =
			imageHeight >= MAX_CONTAINER_HEIGHT ? MAX_CONTAINER_HEIGHT : imageHeight;
		setBorderImageHeight(imageHeight === 0 ? imageHeight : maxImageHeight);

		setIsButtonDefaultPosition(true);
	};

	const getDragButtonPosition = (data) => {
		const resultSecondImageWidth = initialSecondImageWidth() - data.x;
		const resultSecondImageOffset = -initialSecondImageWidth() - data.x;

		setSecondImageWidth(resultSecondImageWidth);
		setIsButtonDefaultPosition(false);
		setXAxisPosition(data.x);
		setSecondImageOffset(checkOffsetValue(resultSecondImageOffset));
	};

	useEffect(() => {
		setDefaultSecondImageParams();

		const onResize = () => {
			setDefaultSecondImageParams();
		};

		window.addEventListener('resize', onResize);

		return () => window.removeEventListener('resize', onResize);
	}, []);

	return (
		<div className={styles.DoubleImage}>
			<div
				className={styles.DoubleImage__Main}
				ref={refMain}
				style={{
					'--double-image-main-width': `${refMain?.current?.offsetWidth}px`,
					'--double-image-second-width': `${secondImageWidth}px`,
					'--double-image-second-offset': `${secondImageOffset}px`,
					'--double-image-border-height': `${borderImageHeight}px`
				}}
			>
				<div className={styles.DoubleImage__WrapImages}>
					<div className={styles.DoubleImage__First}>
						<img
							ref={firstImageRef}
							className={cx(
								styles.DoubleImage__Image,
								styles.DoubleImage__Image_first
							)}
							src={imageSrcFirst}
							alt=""
						/>
					</div>
					<div className={styles.DoubleImage__Second}>
						<div className={styles.DoubleImage__SecondInner}>
							<img
								ref={secondImageRef}
								className={cx(
									styles.DoubleImage__Image,
									styles.DoubleImage__Image_second
								)}
								src={imageSrcSecond}
								alt=""
							/>
						</div>
					</div>
				</div>
				<div className={styles.DoubleImage__DropZone}>
					<div className={styles.DoubleImage__DropZoneInner}>
						<Draggable
							axis="x"
							bounds={draggableZoneBoundaries}
							onDrag={(event, data) => getDragButtonPosition(data)}
							position={currentButtonPosition}
						>
							<button
								type="button"
								className={styles.DoubleImage__Button}
								title="Перетащить кнопку"
								ref={refButton}
							>
								<div className={styles.DoubleImage__ButtonInner}>
									<Icon
										icon="chevronLeftRounded"
										className={styles.DoubleImage__ButtonIcon}
									/>
									<Icon
										icon="chevronRightRounded"
										className={styles.DoubleImage__ButtonIcon}
									/>
								</div>
							</button>
						</Draggable>
					</div>
				</div>
			</div>
			{text && <div className={styles.DoubleImage__Text}>{text}</div>}
		</div>
	);
};

DoubleImage.propTypes = {
	images: PropTypes.shape({
		first: FilePropType,
		second: FilePropType,
		text: PropTypes.string
	})
};

export default DoubleImage;
